import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import DialogComponent from '../../component/dialog/dialog.component';
import ButtonComponent from '../../component/button/button.component';
import {
    flattenOcrAuditFields,
    type AnagraficaOcrAudit,
    type AnagraficaOcrApiField,
    type AnagraficaOcrCreatedByUser,
} from '../../component/archive-form/anagrafica-ocr';
import AnagraficaOcrFieldTable from './anagrafica-ocr-field-table.component';

/**
 * 从创建人摘要拼展示名。
 * @param user 创建人
 * @param fallbackId 回退账号 _id
 */
function formatConfirmedByName(
    user?: AnagraficaOcrCreatedByUser | null,
    fallbackId?: string,
): string {
    if (user) {
        const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
        if (name) {
            return name;
        }
    }
    return (fallbackId || '').trim();
}

/**
 * 从字段行中取首个非空核对人。
 * @param fields 会话或留痕字段
 */
function pickConfirmedByFromFields(
    fields: Array<{ confirmed_by?: string }> | undefined,
): string {
    if (!fields?.length) {
        return '';
    }
    for (const field of fields) {
        const by = String(field.confirmed_by || '').trim();
        if (by) {
            return by;
        }
    }
    return '';
}

/**
 * 查看初始文件识别记录 / 会话字段详情弹窗。
 * 数据可来自餐厅 `ocr_audit` 或列表会话的 `fields[]`。
 */
export default function AnagraficaOcrAuditDialog(props: {
    /** 是否打开。 */
    open: boolean;
    /** 关闭回调。 */
    onClose: () => void;
    /** 弹窗标题；默认「查看初始文件识别记录」。 */
    title?: string;
    /** 餐厅入库留痕；与 sessionFields 二选一优先 audit。 */
    audit?: AnagraficaOcrAudit | null;
    /** 会话 fields 列表（列表页点查看时用）。 */
    sessionFields?: AnagraficaOcrApiField[] | null;
    /** 会话元信息摘要。 */
    meta?: {
        /** 两个地址是否相同；仅会话详情有值。 */
        same_addr?: boolean;
        /** 核对人展示名（优先于字段内 confirmed_by）。 */
        confirmed_by_name?: string;
        /** 核对人账号 _id 回退。 */
        confirmed_by?: string;
        /** 创建人摘要（与列表 created_by_user 同形）。 */
        created_by_user?: AnagraficaOcrCreatedByUser | null;
    } | null;
}) {
    useTranslation();
    const { open, onClose, title, audit, sessionFields, meta } = props;

    const rows = audit?.fields
        ? flattenOcrAuditFields(audit.fields)
        : (sessionFields || []).map((field) => ({
            field_id: field.field_id,
            value_ocr: field.value_ocr,
            value_confirmed: field.value_confirmed,
            confidence: field.confidence,
            confidence_level: field.confidence_level,
            chosen: field.chosen,
            confirmed_by: field.confirmed_by,
            confirmed_at: field.confirmed_at,
        }));
    /**
     * 摘要区核对人：餐厅 ocr_audit.confirmed_by 对象优先，
     * 再会话 meta，最后回退字段内 confirmed_by。
     */
    const confirmedByText = (
        formatConfirmedByName(audit?.confirmed_by)
        || meta?.confirmed_by_name
        || formatConfirmedByName(meta?.created_by_user, meta?.confirmed_by)
        || pickConfirmedByFromFields(rows)
        || ''
    ).trim();

    /** 两个地址是否相同：audit / meta 均可。 */
    const sameAddr = typeof audit?.same_addr === 'boolean'
        ? audit.same_addr
        : meta?.same_addr;

    return (
        <DialogComponent
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            defaultHeaderProps={{
                titleAlign: 'left',
                title: title || i18n.t('anagrafica_ocr_view_initial_record'),
            }}
            footer={(
                <ButtonComponent
                    size="small"
                    variant="contained"
                    iconBtn={false}
                    handleClick={onClose}
                    titleText={i18n.t('anagrafica_ocr_btn_close')}
                >
                    {i18n.t('anagrafica_ocr_btn_close')}
                </ButtonComponent>
            )}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {confirmedByText ? (
                        <Typography variant="body2">
                            {i18n.t('anagrafica_ocr_col_confirmed_by')}
                            {': '}
                            {confirmedByText}
                        </Typography>
                    ) : null}
                    {typeof sameAddr === 'boolean' ? (
                        <Typography variant="body2">
                            {i18n.t('archive_form_same_address')}
                            {': '}
                            {sameAddr
                                ? i18n.t('general_text_yes')
                                : i18n.t('general_text_no')}
                        </Typography>
                    ) : null}
                </Box>
                <AnagraficaOcrFieldTable rows={rows} />
            </Box>
        </DialogComponent>
    );
}
